import { useEffect, useRef, type ReactNode } from "react";
import { create } from "zustand";
import { useNavigate } from "@tanstack/react-router";
import { Activity, AlertCircle, ArrowLeft, Building2, ChartNoAxesCombined, ClipboardList, CreditCard, Download, LayoutDashboard, LogOut, Monitor, Settings2, TrendingUp, UserCog, Users } from "lucide-react";
import { AppShell, Button, IconButton } from "@studio";
import { OrganizationAvatar, OrganizationIdentity } from "@studio-features/meta-crew/console-identity";
import { ConsoleHeader } from "@studio-features/meta-crew/console-header";
import { ConsoleFab } from "@studio-features/meta-crew/console-fab";
import { useAuth } from "../lib/auth";
import { initials, rolesLabel } from "../lib/format";
import type { LocalStatus } from "../lib/native";
import { useResultReviewSummary } from "../lib/queries";
import type { CustomerSummary, Me } from "../lib/types";
import { DesktopStatusBar } from "../screens/desktop-status";
import { StaffNotifications } from "../screens/incidents";
import { CustomerPublishNotifications } from "../screens/customer-publish";
import { adminNavigation, customerNavigation, desktopNavigation, findCategory, type Category } from "./nav";
import { ThemeToggle } from "./freshness";
import { Brand } from "./ui";
import { PiiGrantBar } from "./masked";
import { UploadTray } from "./upload-tray";

const categoryIcons: Record<string, ReactNode> = {
  overview: <LayoutDashboard />,
  customers: <Building2 />,
  runs: <ClipboardList />,
  tasks: <ClipboardList />,
  machines: <Monitor />,
  "billing-ops": <CreditCard />,
  billing: <CreditCard />,
  monitoring: <Activity />,
  marketing: <TrendingUp />,
  usage: <ChartNoAxesCombined />,
  organization: <Users />,
  // 사용자 관리 (관리자 계정·역할): 시안 05be2fc 에서 새로 생긴 카테고리
  users: <UserCog />,
  local: <Settings2 />,
  system: <Settings2 />,
};

/**
 * 사이드바 접힘 상태 (전역). AppShell 은 접힘을 내부 상태로만 갖고 화면을 옮기면 다시 만들어져 기본값으로 돌아간다.
 * 그래서 셸의 sidebar-collapsed 클래스를 지켜보다가 바뀌면 여기에 남기고, 다음 화면의 기본값으로 넘긴다. 브라우저에도 기억한다.
 */
const SIDEBAR_KEY = "meta-crew.sidebarCollapsed";
const useSidebar = create<{ collapsed: boolean; set: (v: boolean) => void }>((set) => ({
  collapsed: (() => {
    try {
      const v = localStorage.getItem(SIDEBAR_KEY);
      return v === null ? true : v === "1";
    } catch {
      return true;
    }
  })(),
  set: (collapsed) => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0");
    } catch {
      /* 비공개 창 등 */
    }
    set({ collapsed });
  },
}));

function useRememberSidebar() {
  const ref = useRef<HTMLDivElement>(null);
  const setCollapsed = useSidebar((s) => s.set);
  useEffect(() => {
    const shell = ref.current?.querySelector(".app-shell");
    if (!shell) return;
    const read = () => {
      const now = shell.classList.contains("sidebar-collapsed");
      if (now !== useSidebar.getState().collapsed) setCollapsed(now);
    };
    const mo = new MutationObserver(read);
    mo.observe(shell, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, [setCollapsed]);
  return ref;
}

type Props = {
  me: Me;
  /** admin: 관리자 웹 · customer: 고객 웹(직원이 고객사를 연 경우 포함) · desktop: 데스크톱 앱(Electron, 머신 계정) */
  scope: "admin" | "customer" | "desktop";
  screen: string;
  customer?: CustomerSummary;
  title?: string;
  /** 시안 e4ed46c: 화면마다 등록 동작 하나를 우측 하단 FAB 로 (작업·머신·고객사 등록, 회원 초대). 권한이 없으면 넘기지 않는다. */
  fab?: { label: string; onClick: () => void };
  /** desktop 만: 이 PC 이름과 로컬 엔진 상태 (하단 상태바). */
  machineName?: string;
  local?: LocalStatus | null;
  nativeAvailable?: boolean;
  children: ReactNode;
};

/**
 * 시안 e4ed46c 의 공통 AppShell. 세 화면면이 같은 틀을 쓰고 메뉴만 다르다.
 * - 범위 구분은 탐색 패널 색으로: 전체 관리(직원)는 네이비, 고객사 범위(고객 계정·직원의 고객사 접근)는 청록, 데스크톱은 중립.
 *   헤더에는 범위 배지·복귀 버튼 없이 현재 메뉴 이름과 (고객사 범위면) 고객사 프로필·이름만. 복귀는 좌측 패널의 "전체 관리로"(직원만).
 * - 사이드바는 PC 에서 기본 접힘(아이콘 레일), 경계선 버튼으로 펼침. 접힌 레일 상단은 관리자면 Meta Crew 로고, 고객사 범위면 고객사 프로필.
 * - 등록 동작은 우측 하단 FAB 하나 (fab).
 */
export function ConsoleShell({ me, scope, screen, customer, title, fab, machineName, local, nativeAvailable = false, children }: Props) {
  const navigate = useNavigate();
  const logout = useAuth((s) => s.logout);
  const sidebarCollapsed = useSidebar((s) => s.collapsed);
  const shellRef = useRememberSidebar();
  const staff = me.isStaff;
  const admin = scope === "admin";
  const desktop = scope === "desktop";
  const adminNav = admin ? adminNavigation(me) : null;
  const categories: Category[] = adminNav ? adminNav.categories : desktop ? desktopNavigation(me, nativeAvailable) : customerNavigation(me);
  const current = findCategory(categories, screen) ?? (screen === "machine" ? categories.find((c) => c.id === "machines") : undefined);
  const heading = title ?? current?.tabs.find((t) => t.id === screen)?.label ?? current?.label ?? "접근할 수 없는 화면";
  // 헤더에는 1뎁스 메뉴 이름 (탭 이름은 본문 탭에 있다).
  const pageTitle = title ?? (desktop && screen === "settings" ? "이 PC 설정" : (current?.label ?? heading));
  // 작업 메뉴에 검토 대기 건수 배지 (직원만, 시안 6a084bb).
  const reviewSummary = useResultReviewSummary(admin ? "" : (customer?.id ?? ""), staff && !desktop && me.permissions.includes("TASK_DETAIL"));
  const pendingReviews = reviewSummary.data?.pending ?? 0;

  const go = (target: string) => {
    if (admin) void navigate({ to: "/admin/$screen", params: { screen: target } });
    // 데스크톱 메뉴의 머신 화면 이름은 "machine" (이 PC 하나). 공통 화면이 부르는 "machines" 를 그쪽으로 보낸다.
    else if (desktop) void navigate({ to: "/bridge/$screen", params: { screen: target === "machines" ? "machine" : target } });
    else if (customer) void navigate({ to: "/c/$customerId/$screen", params: { customerId: customer.id, screen: target } });
  };
  const onNavigate = (id: string) => {
    const cat = categories.find((c) => c.id === id);
    go(cat?.tabs[0]?.id ?? id);
  };

  const state = customer?.status ?? "active";
  const context = admin ? undefined : (
    <section className="mcc-customer-context" aria-label={desktop ? "이 PC" : staff ? "현재 고객사" : "내 조직"}>
      <OrganizationIdentity name={desktop ? (machineName ?? "이 PC") : (customer?.name ?? "…")} initials={desktop ? "PC" : initials(customer?.name ?? "")} state={state} compact />
      {staff && !desktop && (
        <Button className="mcc-customer-context-back" size="sm" variant="ghost" onClick={() => void navigate({ to: "/admin/$screen", params: { screen: "customers" } })}>
          <ArrowLeft size={14} aria-hidden="true" />
          전체 관리로
        </Button>
      )}
    </section>
  );

  const banner = !admin && customer && state !== "active" && <ServiceBanner customer={customer} canPay={!desktop && me.permissions.includes("BILLING_READ")} onBilling={() => go("billing")} />;

  return (
    <div ref={shellRef} className={`admin-demo meta-crew-admin mcc-console mcc-scope-${desktop ? "machine" : scope}${fab ? " mcc-has-fab" : ""}${desktop ? " lwn-host" : ""}`}>
      <AppShell
        mobileNavigation="drawer"
        sidebarCollapsible
        sidebarCollapsedMode="icons"
        collapsedBrand={
          <span className="meta-crew-brand" aria-label="Meta Crew">
            <span>
              <Activity size={19} />
            </span>
          </span>
        }
        collapsedOrganization={admin ? undefined : <OrganizationAvatar name={desktop ? (machineName ?? "이 PC") : (customer?.name ?? "고객사")} rail />}
        defaultSidebarCollapsed={sidebarCollapsed}
        showBrand={admin}
        brand={<Brand />}
        organization={context ? () => context : undefined}
        organizationSubtitle="Meta Crew"
        userName={desktop ? (machineName ?? "이 PC") : (me.name ?? me.email ?? "사용자")}
        userTeam={rolesLabel(me)}
        items={categories.map((c) => ({ id: c.id, label: c.label, icon: categoryIcons[c.id], ...(["runs", "tasks"].includes(c.id) && pendingReviews ? { count: pendingReviews } : {}) }))}
        active={current?.id ?? ""}
        onNavigate={onNavigate}
        header={
          <ConsoleHeader title={pageTitle} organization={scope === "customer" ? customer?.name : undefined}>
              <ThemeToggle />
              {staff && !desktop && <StaffNotifications me={me} onNavigate={(s) => void navigate({ to: "/admin/$screen", params: { screen: s } })} />}
              {/* 시안 951f2f6: 고객 발행 알림 (수정 요청·반려·발행됨·새 답글). 고객 회원만, 자기 요청만. */}
              {!staff && !desktop && scope === "customer" && customer?.id && me.permissions.includes("PUBLISH_READ") && <CustomerPublishNotifications customerId={customer.id} />}
              {/* 데스크톱은 머신 계정이라 로그아웃이 없다 (사람 로그인 없음) */}
              {!desktop && (
                <IconButton
                  label="로그아웃"
                  onClick={() => {
                    void logout().then(() => navigate({ to: "/login", search: { redirect: undefined } }));
                  }}
                >
                  <LogOut size={17} />
                </IconButton>
              )}
          </ConsoleHeader>
        }
      >
        <div className="admin-content meta-crew-content mcc-content">
          {banner}
          {/* 개인정보 보기 허가 중 · 남은 시간 · 끝내기 (요청서 26절) */}
          <PiiGrantBar />
          {desktop && <UpdateBanner update={local?.update} />}
          {desktop && local?.engineError && (
            <div className="mcc-banner mcc-banner-danger">
              <AlertCircle size={18} />
              <div>
                <strong>로컬 엔진 응답을 확인하고 있습니다</strong>
                <p>하단 상태바에서 상세 오류와 로그를 확인하세요. 본문은 마지막 중앙 수신 정보를 표시합니다.</p>
              </div>
            </div>
          )}
          <h1 className="sr-only">{heading}</h1>
          {current && current.tabs.length > 1 && (
            <div className="mcc-category-tabs">
              <div className="meta-crew-tabs" role="group" aria-label="화면 분류">
                {current.tabs.map((t) => (
                  <button key={t.id} type="button" aria-pressed={t.id === screen} className={t.id === screen ? "active" : ""} onClick={() => go(t.id)}>
                    {t.label}
                    {t.id === "reviews" && pendingReviews > 0 && <span className="nav-count">{pendingReviews}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {current || screen === "machine" ? children : <NoAccess onHome={() => go("overview")} />}
        </div>
      </AppShell>
      {fab && <ConsoleFab {...fab} />}
      {/* 요청서 34절: 정형화 업로드 트레이 — 직원 콘솔에서 화면과 무관하게 유지 */}
      {staff && !desktop && <UploadTray />}
      {desktop && nativeAvailable && <DesktopStatusBar local={local ?? null} machineName={machineName ?? "이 PC"} onNavigate={go} />}
    </div>
  );
}

function NoAccess({ onHome }: { onHome: () => void }) {
  return (
    <div className="mcc-empty">
      <AlertCircle />
      <h2>이 화면의 접근 권한이 없습니다</h2>
      <Button onClick={onHome}>현황으로 이동</Button>
    </div>
  );
}

/**
 * 원격 업데이트 진행 (2026-10-20 사용자 요청: 진행이 안 보여 설치 중인지 알 수 없음).
 * 다운로드·확인은 엔진이 알려 주는 %로, 설치 단계에서는 앱이 꺼지고 설치 창이 진행을 보여 준다.
 */
function UpdateBanner({ update }: { update: LocalStatus["update"] }) {
  if (!update || !["pending", "downloading", "installing", "failed"].includes(update.state)) return null;
  const v = update.version ? ` ${update.version}` : "";
  const pct = update.progressPct ?? 0;
  const [title, text] =
    update.state === "pending"
      ? [`업데이트${v} 준비 중`, update.message ?? "진행 중인 작업이 끝나면 내려받습니다. 프로그램을 끄지 마세요."]
      : update.state === "downloading"
        ? [`업데이트${v} 내려받는 중 · ${pct}%`, "내려받기와 확인이 끝나면 프로그램이 잠시 꺼지고 설치 창이 열립니다."]
        : update.state === "installing"
          ? [`업데이트${v} 설치를 시작합니다`, "프로그램이 잠시 꺼지고 설치 창에 진행이 표시됩니다. 설치가 끝나면 자동으로 다시 켜집니다."]
          : [`업데이트${v} 실패`, update.message ?? "담당자에게 알려 주세요. 지금 버전으로 계속 동작합니다."];
  return (
    <div className={`mcc-banner ${update.state === "failed" ? "mcc-banner-danger" : ""}`} role="status">
      {update.state === "failed" ? <AlertCircle size={18} /> : <Download size={18} />}
      <div style={{ flex: 1 }}>
        <strong>{title}</strong>
        <p>{text}</p>
        {update.state !== "failed" && (
          <progress style={{ width: "100%" }} max={100} value={update.state === "downloading" ? pct : update.state === "installing" ? 100 : undefined} aria-label="업데이트 진행" />
        )}
      </div>
    </div>
  );
}

function ServiceBanner({ customer, canPay, onBilling }: { customer: CustomerSummary; canPay: boolean; onBilling: () => void }) {
  const s = customer.status;
  const sub = customer.subscription;
  return (
    <div className={`mcc-banner ${s === "suspended" || s === "terminated" ? "mcc-banner-danger" : ""}`}>
      <AlertCircle size={20} />
      <div>
        <strong>
          {s === "overdue"
            ? `결제 확인이 필요합니다${sub ? ` · 기간 종료 ${new Date(sub.currentPeriodEnd).toLocaleDateString("ko-KR")}` : ""}`
            : s === "suspended"
              ? "서비스가 정지되었습니다"
              : "서비스가 해지되었습니다"}
        </strong>
        <p>
          {s === "overdue"
            ? "유예 기간에는 정상 이용할 수 있습니다. 결제 또는 연장 후 자동으로 정상 상태가 됩니다."
            : `새 작업과 예약 배정이 중지되었습니다. 상태 조회는 계속됩니다.${customer.statusReason ? ` 사유: ${customer.statusReason}` : ""}`}
          {customer.support ? ` 문의: ${customer.support}` : " 담당자에게 문의해 주세요."}
        </p>
      </div>
      {canPay && s !== "terminated" && (
        <Button variant="secondary" size="sm" onClick={onBilling}>
          결제 확인
        </Button>
      )}
    </div>
  );
}
